import { point } from '../elements/point';
import type { Point } from '../elements/point';
import { vector } from '../elements/vector';
import { isNumber, roundTo, roundTo2 } from '../utils/numbers';

type PointFn<TReturn> = ((p: Point) => TReturn) &
  ((x: number, y: number) => TReturn);

/**
 * Turn a function that accepts a Point to a function that accepts Point or x: number, y: number
 */
const makePointArg =
  <TReturn>(fn: (p: Point) => TReturn): PointFn<TReturn> =>
  (px: Point | number, y?: number) =>
    fn(isNumber(px) ? point.create(px, y) : px);

/**
 * There are four variants of how to connect two points with an arc,
 * given arc ellipse shape (radiuses and rotation)
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths#Arcs
 */
interface ArcType {
  large: boolean;
  /**
   * If true, the arc goes on the left side of the segment (current position -> target point),
   * otherwise it goes on the right side.
   */
  left: boolean;
}

export const ARC_LARGE_LEFT: ArcType = { large: true, left: true };
export const ARC_LARGE_RIGHT: ArcType = { large: true, left: false };
export const ARC_SMALL_LEFT: ArcType = { large: false, left: true };
export const ARC_SMALL_RIGHT: ArcType = { large: false, left: false };

export class SvgPathBuilder {
  private path: string[] = [];
  private abs = true;
  private roundFn = roundTo2;
  private scale = 1;
  private cspace: string;
  private ccomma: string;

  constructor(private compact = false) {
    this.cspace = this.compact ? '' : ' ';
    this.ccomma = this.compact ? ' ' : ', ';
  }

  /**
   * Set fraction digits of coordinates in resulting SVG path
   *
   * You can use 0 to avoid subpixel rendering when SVG is rendered
   * in its native size. Otherwise, the more digits you allow, the more precise
   * the result will be, but obviously larger in terms of data size.
   */
  public setFractionDigits = (fractionDigits: number): this => {
    this.roundFn = roundTo(fractionDigits);
    return this;
  };

  public setScale = (scale: number): this => {
    this.scale = scale;
    return this;
  };

  private s = (n: number): number => this.roundFn(this.scale * n);
  private sp = (p: Point): string => {
    const { x, y } = vector.mult(p, this.scale);
    return `${this.roundFn(x)} ${this.roundFn(y)}`;
  };

  private push = (str: string) => {
    this.path.push(str);
    return this;
  };

  public relative = (): this => {
    this.abs = false;
    return this;
  };

  public absolute = (): this => {
    this.abs = true;
    return this;
  };

  private command = (letter: string) =>
    this.abs ? letter : letter.toLowerCase();

  public moveTo = makePointArg((p: Point): this =>
    this.push(`${this.command('M')}${this.cspace}${this.sp(p)}`),
  );

  public lineTo = makePointArg((p: Point): this =>
    this.push(`${this.command('L')}${this.cspace}${this.sp(p)}`),
  );

  public hLineTo = (x: number): this =>
    this.push(`${this.command('H')}${this.cspace}${this.s(x)}`);

  public vLineTo = (y: number): this =>
    this.push(`${this.command('V')}${this.cspace}${this.s(y)}`);

  public close = (): this => this.push('z');

  public arcTo = (
    rx: number,
    ry: number,
    xRotation: number, // intentionally not rounded in the result as it is not a coordinate
    arcType: ArcType,
  ): PointFn<this> =>
    makePointArg((p: Point) =>
      this.push(
        `${this.command('A')}${
          this.cspace
        }${this.s(rx)} ${this.s(ry)} ${xRotation} ${arcType.large ? 1 : 0} ${
          arcType.left ? 1 : 0
        } ${this.sp(p)}`,
      ),
    );

  public circleTo = (radius: number, arcType: ArcType): PointFn<this> =>
    this.arcTo(radius, radius, 0, arcType);

  public cubicBezierTo = makePointArg((controlStart: Point) =>
    makePointArg((controlEnd: Point) =>
      makePointArg((end: Point) =>
        this.push(
          `${this.command('C')}${this.cspace}${this.sp(controlStart)}${
            this.ccomma
          }${this.sp(controlEnd)}${this.ccomma}${this.sp(end)}`,
        ),
      ),
    ),
  );

  public quadraticBezierTo = makePointArg((control: Point) =>
    makePointArg((end: Point) =>
      this.push(
        `${this.command('Q')}${this.cspace}${this.sp(control)}${this.ccomma}${this.sp(end)}`,
      ),
    ),
  );

  public build = (): string => this.path.join(this.cspace);

  public buildClosed = (): string => this.close().build();
}
